import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { AlertService, DailyService } from '@app/core';
import { Resp } from '@app/core/services/api.service';
import { AlertDailyComponent } from '@app/modules/eclass/components/modal-daily/alert-daily.component';
import { ModalDailyTab, SidenavDailyShow } from '@app/modules/eclass/components/modal-daily/base';
import { SidenavDailyComponent } from '@app/modules/eclass/components/modal-daily/sidenav-daily.component';
import { SHARED_MODULES } from '@app/shared';
import { notNull } from '@app/shared/utils';
import { format } from 'date-fns';
import { isEmpty, isObject } from 'lodash';
import { catchError, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'app-modal-duplicate-daily',
  imports: [SHARED_MODULES, SidenavDailyComponent],
  templateUrl: './modal-duplicate-daily.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDuplicateDailyComponent {
  private readonly _dailyService = inject(DailyService);
  private readonly _daily = inject(DIALOG_DATA);
  private readonly _dialog = inject(Dialog);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);

  protected targets: SidenavDailyShow[] = [];
  protected loading = signal<boolean>(false);

  protected get data(): any {
    return {
      type: ModalDailyTab.Daily,
      title: 'eclass.aula.duplicar-aula',
      titleElement: this._daily.title,
      targetsLabel: 'eclass.aula.duplicar-para',
      publishLabel: 'eclass.aula.publicar',
      resetForm: () => null,
      markAllAsTouched: () => null,
      patchValue: () => null,
      onSave: this.onDuplicate,
      form: this._formBuilder.group({}),
      showSidenavEnd: false,
      sidenavEndData: null,
      targets: [],
      submitted$: of(),
      value: this._daily,
    };
  }

  protected onTargets(targets: SidenavDailyShow[]) {
    this.targets = targets;
  }

  protected onClose(): void {
    this._dialogRef.close();
  }

  protected onSave(): void {
    this.data
      .onSave({ ...this.data.value, targets: this.targets })
      .pipe(take(1), notNull())
      .subscribe(() => {
        this._dialogRef.close(true);
      });
  }

  protected onDuplicate = (params: any): Observable<boolean> =>
    this.alertMessage(params).pipe(switchMap((confirm) => (confirm ? this.duplicate(params) : of(false))));

  private duplicate(params: any): Observable<boolean> {
    this.loading.set(true);
    const target = params.targets.map((target: SidenavDailyShow) => ({
      id: target.id,
      entity_id: target.entity.id,
      group_id: target.group.id,
      subject_id: target.subject.id,
      date: format(target.date, 'yyyy-MM-dd'),
    }));
    const data = {
      ...params,
      target,
    };
    return this.store(target, data).pipe(
      take(1),
      tap((result) => !!result && this._alertService.success({ message: 'geral.salvo-com-sucesso' })),
      finalize(() => this.loading.set(false)),
    );
  }

  private store(targets: SidenavDailyShow[], data: any): Observable<boolean> {
    return this.handleResponse(
      this._dailyService.store({
        ...data,
        attachments: data?.attachments?.map(({ id, ...attachment }) => attachment),
        targets,
      }),
    );
  }

  private handleResponse(observable$: Observable<Resp<any>>): Observable<boolean> {
    return observable$.pipe(
      catchError((data) => {
        const message = data.msg || data.error?.msg;
        return of({ ret: 0, data: isObject(message) ? JSON.stringify(message) : message });
      }),
      switchMap((result) => {
        if (!result.ret) {
          const message = !!result.data ? { innerHtml: result.data } : { message: 'geral.erro-ao-salvar' };
          return this._alertService.alert(message).pipe(map(() => false));
        }
        return of(true);
      }),
    );
  }

  private alertMessage(params: any): Observable<boolean> {
    const withAlerts = params.targets.filter(({ alert }) => !!alert);
    if (!isEmpty(withAlerts)) {
      return this._dialog.open<boolean>(AlertDailyComponent, { data: withAlerts }).closed;
    }
    return this._alertService.confirm({ message: 'eclass.aula.salvar' });
  }
}
